import { catchError } from 'rxjs/operators'
import { BaseError, Http404Error } from 'src/utils/errors'
import { MatchingRuleInfo, SelectionMatchType } from 'src/utils/types'
import { SyncSourceProviderService } from '../account/services/sync-source.service'
import { ApiService } from '../services/api.service'
import { UserSelectableResource } from './app-resources-selector/app-resources-selector.component'
import { computeResourceDisplayName } from './utils'
import { AwsSourceResourceDto } from '../services/dtos'
import { getResourceTypeDetails, ResourceTypeDetails } from 'src/utils/resource-types'

export class SelectableApplicationResource implements UserSelectableResource {
  tags: Map<string, string>
  arnMatch?: boolean
  matchingRules?: Map<string, MatchingRuleInfo>
  dependeeOf?: Set<string>
  weakDependeeOf?: Set<string>
  suppressedWeakDependeeOf?: Set<string>
  directDependeeOf?: Set<string>
  isExcluded?: boolean
  resourceTypeDetails: ResourceTypeDetails

  constructor(public sourceRes: AwsSourceResourceDto) {
    this.tags = new Map(sourceRes.tags.map((tagDto) => [tagDto.name, tagDto.value] as [string, string]))
    this.matchingRules = new Map<string, MatchingRuleInfo>()
    this.dependeeOf = new Set<string>()
    this.weakDependeeOf = new Set<string>()
    this.suppressedWeakDependeeOf = new Set<string>()
    this.directDependeeOf = new Set<string>()
    this.isExcluded = false
    this.resourceTypeDetails = getResourceTypeDetails({
      resourceType: sourceRes.type,
      resourceVariant: sourceRes.variant,
    })
  }

  get resourceArn() {
    return this.sourceRes.arn
  }

  get resourceId() {
    return this.sourceRes.id
  }

  get resourceName() {
    return this.sourceRes.name
  }

  get resourceDisplayName() {
    return this.sourceRes.displayName
  }

  get resourceType() {
    return this.sourceRes.type
  }

  get resourceVariant() {
    return this.sourceRes.variant
  }

  get parentArn() {
    return this.sourceRes.parentArn
  }

  get descendantCount() {
    return this.sourceRes.descendantCount
  }

  get isContainer() {
    return this.sourceRes.isContainer
  }

  get dependees() {
    return this.sourceRes.dependees
  }

  get weakDependees() {
    return this.sourceRes.weakDependees
  }

  get directDependees() {
    return this.sourceRes.directDependees
  }

  get resourceGroup() {
    return this.sourceRes.resourceGroup
  }

  get baseDisplayName() {
    return computeResourceDisplayName(this.resourceId, this.resourceName, this.resourceDisplayName)
  }

  get displayName() {
    const baseName = this.baseDisplayName
    const resourceGroup = this.resourceGroup
    return resourceGroup ? `${resourceGroup}/${baseName}` : baseName
  }

  get displayNameAsChild() {
    if (this.resourceType == 'k8sResource' && this.resourceVariant) {
      return `${this.resourceVariant}: ${this.baseDisplayName}`
    } else {
      // Get display name for the resource type
      return `${this.resourceTypeDetails.displayName}: ${this.baseDisplayName}`
    }
  }

  get displayResourceType() {
    return this.resourceTypeDetails.displayName
  }

  get selectionType() {
    let selectionType = SelectionMatchType.NO_MATCH
    if (this.arnMatch) {
      selectionType |= SelectionMatchType.ARN_MATCH
    }
    if (this.matchingRules.size > 0) {
      selectionType |= SelectionMatchType.TAG_MATCH
    }
    if (this.weakDependeeOf.size > 0) {
      selectionType |= SelectionMatchType.WEAK_TRANSITIVE_MATCH
    }
    if (this.dependeeOf.size > 0) {
      selectionType |= SelectionMatchType.TRANSITIVE_MATCH
    }
    if (this.directDependeeOf.size > 0) {
      selectionType |= SelectionMatchType.DIRECT_DEPENDEE_MATCH
    }
    return selectionType
  }

  get shouldRender() {
    return this.resourceTypeDetails.displayForSelection
  }
}

export function ensureAwsAccount$(apiService: ApiService, accountId: string, awsAccountId: string) {
  return apiService.getAwsAccount(accountId, awsAccountId).pipe(
    catchError((error: BaseError) => {
      if (error instanceof Http404Error) {
        return apiService.createAwsAccount(accountId, awsAccountId)
      }
      throw error
    })
  )
}

export function initializeSyncSource$(
  syncSourceProviderService: SyncSourceProviderService,
  accountId: string,
  endpointUri: string
) {
  return syncSourceProviderService.loadSyncSource$(accountId, endpointUri)
}
